<template>
    <div class="my-10" id="sermons">
        <!-- search bar -->
        <label class="relative block">
            <span @click="searchSermons" class="cursor-pointer absolute inset-y-0 left-0 flex items-center pl-3">
                <i class="fa-solid fa-magnifying-glass"></i>
            </span>
            <input 
                v-model="searchBarVal"
                class="w-full bg-white placeholder:font-italitc border border-slate-300 rounded-full py-2 pl-10 pr-4 focus:outline-none"
                placeholder="Search sermon" type="search" name="search"
                @keyup.enter="searchSermons" 
            />
        </label>

        <div v-if="sermonYears.length > 0" class="flex mt-3">
            <div class="flex gap-x-3 overflow-auto h-hit">
                <div class="px-3 py-1.5 text-center flex text-sm hover:cursor-pointer border rounded-full whitespace-nowrap"
                    :class="{'text-orange-500 border-orange-500': selectedYear == year, 'text-black border-slate-400': selectedYear != year }"
                    v-for="year in sermonYearsWithAll" @click="sortSermonByYear(year)">
                    {{ year }} {{ year == 'All' ? '' : 'Messages'}}
                </div>
            </div>
        </div>
        
        <!-- years -->
        <div v-if="!isLoading">

            <div v-if="isSermonSearched" class="text-center mt-8">
                <p v-if="sermons.length > 0" class="text-lg font-medium">Showing sermons containing "{{ searchBarVal }}".</p>
                <p v-else class="text-lg font-medium">No sermons found matching your search.</p>

                <div 
                    class="underline cursor-pointer hover:text-orange-500" 
                    @click="clearSearch"
                >Clear search</div>
            </div>


            <div class="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
                <div v-for="sermon in sermons">
                    <RouterLink :to="`/sermons/${sermon.id}`" class="h-40 section-image bg-slate-200">
                        <img :src="sermon.data.cover_image" class="w-full bg-slate-200" :alt="sermon.data.title + ' image'"/>
                        <div class="hidden md:block">
                            <div class="text-lg font-bold">{{ sermon.data.title }}</div>
                            <div class="text-sm text-[#f97316]">
                                <span class="font-bold">Upload date:</span> 
                                {{ new Date(sermon.data.date_created).toDateString() }}
                            </div>
                        </div>
                    </RouterLink>
                </div>
            </div>

            <!-- Load More Button -->
            <div v-if="hasMoreSermons && !isLoadingMore" class="text-center mt-8">
                <button @click="loadMore" class="bg-orange-500 text-white px-6 py-2 rounded-full hover:bg-orange-600 transition-colors">
                    Load More Sermons
                </button>
            </div>
            <div v-if="isLoadingMore" class="text-center mt-8">
                <Loader />
            </div>
        </div>

        <div v-else class="text-center mt-8">
            <Loader />
        </div>
    </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { useDataStore } from '../../stores/useData';
import { onMounted, nextTick, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router';
import Loader from '../Loader.vue';

let dataStore  = useDataStore()

let { getSermons, loadMoreSermons, getSermonYears } = dataStore
let { sermons, hasMoreSermons, sermonYears } = storeToRefs(dataStore)

let router = useRouter()
let route = useRoute()

let sermonYearsWithAll = ref(['All'])
let selectedYear = ref('All')
let isLoading = ref(true)
let isLoadingMore = ref(false)
let searchBarVal = ref('')
let isSermonSearched = ref(false)

async function loadMore() {
    isLoadingMore.value = true
    await loadMoreSermons(selectedYear.value == 'All' ? null : selectedYear.value)
    isLoadingMore.value = false
}

async function scrollToSermons() {
    await nextTick()
    const target = document.getElementById('sermons')
    if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
}

async function sortSermonByYear(year = 'All') {
    if (year == selectedYear.value) return

    // Silently update the URL query without triggering navigation
    const newQuery = { ...route.query, year }
    const newUrl = `${window.location.pathname}?${new URLSearchParams(newQuery).toString()}`
    window.history.replaceState(null, '', newUrl)

    isLoading.value = true
    
    selectedYear.value = year
    sermons.value = null
    
    await getSermons({ year: year == 'All' ? null : parseInt(year), query: null, pagination: { limit: 20 } })

    isLoading.value = false
    await scrollToSermons()
}

async function searchSermons() {
    if (!searchBarVal.value || searchBarVal.value.trim() === '') return

    isSermonSearched.value = true
    isLoading.value = true
    selectedYear.value = 'All'

    const newQuery = { ...route.query, year: 'All' }
    const newUrl = `${window.location.pathname}?${new URLSearchParams(newQuery).toString()}`
    window.history.replaceState(null, '', newUrl)

    await getSermons({ year: null, query: searchBarVal.value, pagination: { limit: 20 } })

    isLoading.value = false
}

async function clearSearch() {
    searchBarVal.value = null
    isSermonSearched.value = false
    isLoading.value = true
    sermons.value = null
    selectedYear.value = 'All'
    
    await getSermons({ year: 'All', query: null, pagination: { limit: 20 } })

    isLoading.value = false

}

onMounted(async () => {
    await getSermonYears()
    sermonYearsWithAll.value = ['All', ...sermonYears.value]

    selectedYear.value = route.query.year ? route.query.year : 'All'

    let numberYear = parseInt(selectedYear.value)

    await getSermons({ year: selectedYear.value == 'All' ? null : numberYear, query: null, pagination: { limit: 20 } })

    isLoading.value = false
})

</script>

<style scoped>
.section-image {
    background-image: url('../../assets/images/placeholder.jpg');
    background-size: contain;
    background-position: center;
}
</style>